import { useCallback, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import { useAuth } from "@/auth/AuthProvider";
import { queryKeys } from "@/lib/queryKeys";

export interface ProductActivation {
  id: string;
  org_id: string;
  product: string;
  status: "active" | "trial" | "suspended";
  config: Record<string, unknown>;
  activated_at: string;
  expires_at: string | null;
  created_at: string;
  updated_at: string;
}

export function useOrgProducts() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const orgId = user?.org_id ?? null;

  const query = useQuery({
    queryKey: queryKeys.orgProducts.list(orgId ?? ""),
    queryFn: () => api<ProductActivation[]>(`/api/v2/orgs/${orgId}/products`),
    enabled: orgId !== null,
  });

  const activateMutation = useMutation({
    mutationFn: (product: string) =>
      api<ProductActivation>(`/api/v2/orgs/${orgId}/products`, {
        method: "POST",
        body: JSON.stringify({ product, status: "active" }),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.orgProducts.list(orgId ?? ""),
      });
    },
  });

  const activateProduct = useCallback(
    async (product: string) => {
      if (!orgId) return;
      await activateMutation.mutateAsync(product);
    },
    [orgId, activateMutation],
  );

  const products = useMemo(() => query.data ?? [], [query.data]);

  const isProductActive = useCallback(
    (product: string) => {
      const p = products.find((a) => a.product === product);
      return p?.status === "active" || p?.status === "trial";
    },
    [products],
  );

  return {
    products,
    loading: query.isLoading,
    activateProduct,
    isProductActive,
  };
}
